<template>
  <div>
    <data-table :service="LanguageService"
                :entity="entity"
                :columns="columns"
                operations="add"
                :go-to-details-on-row-click="false"
                :go-to-add-on-click="false"
                :show-pagination="false"
                :disable-permission-checks="true"
                :show-confirm-before-delete="false"
                :clickable="false"
                @on-add="addRow"
                @on-edit="editRow"
                ref="table">
      <template v-slot:primary="{row, index}">
        <div class="flex items-center">
          <base-switch :value="row.primary"
                       @input="(val) => setPrimaryLanguage(val, row, index)"
                       on-text="YES"
                       off-text="NO">
          </base-switch>
        </div>
      </template>
      <template v-slot:active="{row, index}">
        <div class="flex items-center">
          <base-switch :value="row.active"
                       @input="(val) => updateLanguage(val, 'active', row, index)"
                       on-text="YES"
                       off-text="NO">
          </base-switch>
        </div>
      </template>

      <template v-slot:flag="{row}">
        <img v-if="row.flag"
             :src="`static/img/countries/${row.flag}.svg`"
             :alt="row.name"
             class="w-8 h-8"
        >
        <span v-else>- -</span>
      </template>

      <template v-slot:extra-buttons="{row}">
        <tooltip-icon-button icon="far fa-edit"
                             :content="$t('generic.edit')"
                             @click.stop="editRow(row)">
        </tooltip-icon-button>
      </template>
    </data-table>

    <modal :show.sync="showModal">
      <h6 slot="header">{{rowToEdit.id ? $t('languages.edit.title') : $t('languages.create.title')}}</h6>
      <form @submit.prevent="addOrUpdateLanguage">
        <fg-input :label="$t('languages.create.locale')"
                  required
                  :error="getError($t('languages.create.locale'))">
          <el-select v-model="rowToEdit.locale" class="select-default">
            <el-option v-for="code in languageCodes"
                       :key="code.label"
                       v-bind="code"
                       class="select-default"
            />
          </el-select>
          <small slot="infoBlock">
            {{$t('languages.create.tip')}}
          </small>
        </fg-input>

        <fg-input :placeholder="$t('languages.create.name')"
                  :name="$t('languages.create.name')"
                  :label="$t('languages.create.name')"
                  :error="getError($t('languages.create.name'))"
                  v-validate="'required'"
                  v-model="rowToEdit.name">
        </fg-input>

        <fg-input :placeholder="$t('languages.create.flag')"
                  :name="$t('languages.create.flag')"
                  :label="$t('languages.create.flag')"
                  :error="getError($t('languages.create.flag'))">
          <country-flag-select v-model="rowToEdit.flag"
                               v-validate="'required'">
          </country-flag-select>
        </fg-input>

        <fg-input :placeholder="$t('languages.create.active')"
                  :name="$t('languages.create.active')"
                  :label="$t('languages.create.active')"
                  :error="getError($t('languages.create.active'))">
          <base-switch v-model="rowToEdit.active">
          </base-switch>
        </fg-input>
        <div class="flex w-full justify-center">
          <submit-button native-type="submit" :loading="saving"/>
        </div>
      </form>
    </modal>
  </div>
</template>
<script>
import LanguageSelect from 'src/components/LanguageSelect'
import CountryFlagSelect from 'src/components/CountryFlagSelect'
import LanguageService from 'src/services/LanguageService'
import TooltipIconButton from 'src/components/Buttons/TooltipIconButton'
import { Select, Option } from 'element-ui'
import entities from 'src/config/entities'

export default {
  name: 'language-settings',
  components: {
    LanguageSelect,
    CountryFlagSelect,
    TooltipIconButton,
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data () {
    return {
      LanguageService,
      entity: entities.COUNTRY,
      saving: false,
      showModal: false,
      selectedLocale: this.$i18n.locale,
      rowToEdit: {
        locale: 'nl',
        primary: false,
        active: true,
        flag: 'nl',
        iso: 'nl',
      },
      languageCodes: [
        {
          label: 'en',
          value: 'en'
        },
        {
          label: 'nl',
          value: 'nl'
        },
        {
          label: 'ro',
          value: 'ro'
        },
      ],
      languageFlags: []
    }
  },
  computed: {
    columns () {
      return [
        {
          prop: 'locale',
          label: this.$t('settings.language.labels.locale'),
          minWidth: '120px',
        },
        {
          prop: 'name',
          label: this.$t('settings.language.labels.name'),
          minWidth: '140px'
        },
        {
          prop: 'flag',
          label: this.$t('settings.language.labels.flag'),
          minWidth: '140px'
        },
        {
          prop: 'primary',
          label: this.$t('settings.language.labels.primary'),
          minWidth: '80px'
        },
        {
          prop: 'active',
          label: this.$t('settings.language.labels.active'),
          minWidth: '80px'
        }
      ]
    }
  },
  methods: {
    async updateLanguage (evt, key, row, index = 0) {
      let value = evt.target ? evt.target.value : evt
      this.saving = true
      let id = row.id
      if (id) {
        let data = {
          ...row,
          [key]: value
        }
        await LanguageService.update(id, data)
        row[key] = value
      } else {
        row[key] = value
      }
      this.saving = false
    },
    async disablePrevPrimary () {
      let currentPrimary = this.$refs.table.tableData.find(row => row.primary === true)
      if (!currentPrimary) return
      currentPrimary.primary = false
      await LanguageService.update(currentPrimary.id, currentPrimary)
    },
    async setPrimaryLanguage (val, row, index) {
      if (val) {
        await this.disablePrevPrimary()
        row.primary = val
        if (!row.active) {
          row.active = true
        }
        await LanguageService.update(row.id, row)
        this.$refs.table.tableData.splice(index, 1, row)
      }
    },
    async trySave (row, index) {
      if (!row.id) {
        await this.saveCountry(row, index)
      }
    },
    async saveCountry (row, index = 0) {
      let result = await LanguageService.create(row)
      this.$refs.table.tableData.splice(index, 1, result.data)
    },
    async addOrUpdateLanguage () {
      try {
        this.saving = true
        if (!this.rowToEdit.iso) {
          this.rowToEdit.iso = this.rowToEdit.locale
        }
        if (this.rowToEdit.locale === 'en') {
          this.rowToEdit.iso = `${this.rowToEdit.iso}-${this.rowToEdit.flag}`
        }
        if (!this.rowToEdit.id) {
          await LanguageService.create(this.rowToEdit)
        } else {
          await LanguageService.update(this.rowToEdit.id, this.rowToEdit)
        }
        this.showModal = false
        this.$refs.table.refresh()
      } finally {
        this.saving = false
      }
    },
    addRow () {
      this.showModal = true
      this.rowToEdit = {
        locale: 'nl',
        primary: false,
        active: true,
        flag: 'nl',
        iso: 'nl',
      }
    },
    editRow(row) {
      this.showModal = true
      this.rowToEdit = row
    }
  }
}
</script>
<style>
</style>
